import React from 'react'
import cls from 'classnames'
import styled from 'styled-components'

export const STYLE_TYPES = {
  NEUTRAL: 'neutral',
  EMPHASIS: 'emphasis',
}

const StyledActionIcon = styled.button`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 4px;
  border-radius: 4px;
  background-color: transparent;
  border: 0;
  transition: background-color 0.2s ease, fill 0.2s ease;
  width: 24px;
  height: 24px;

  & > div {
    width: ${({ width }) => width || '20px'};
    height: ${({ height }) => height || '20px'};
  }

  svg > path {
    fill: ${({ theme, variant }) =>
      variant === STYLE_TYPES.EMPHASIS ? theme.primary : theme.ds_gray_2};
  }

  &:disabled {
    cursor: default;

    svg > path {
      fill: ${({ theme }) => theme.ds_gray_5};
    }
  }

  &:not(:disabled) {
    cursor: pointer;

    &:hover,
    &.__hover,
    &:active {
      background-color: ${({ theme }) => theme.ds_blue_7};

      svg > path {
        fill: ${({ theme }) => theme.ds_blue_2};
      }
    }
  }

  ${({ active, theme }) =>
    active &&
    `
      background-color: ${theme.ds_blue_7};

      svg > path {
        fill: ${theme.ds_blue_2};
      }
    `}
`

/**
 * @typedef {Object} ActionIconStyledProps
 * @property {string} id - Required unique identifier for the icon component.
 * @property {string} [className] - Optional CSS class name for custom styling.
 * @property {string} dataTestId -  Unique identifier for test automation (data-testid).
 * @property {string} [title] - Accessible label or tooltip text shown on hover.
 * @property {() => void} [onClick] - Callback function triggered on click.
 * @property {boolean} [disabled] - Whether the icon is disabled.
 * @property {boolean} [isLink] - Whether the component should be rendered as a link.
 * @property {boolean} [active] - Whether the icon is currently active/selected.
 * @property {React.ElementType} Icon - Required React component for the icon.
 * @property {keyof typeof STYLE_TYPES} [styleType] - Style type from STYLE_TYPES enum.
 * @property {Object} [iconProps] - Additional props to pass to the icon component.
 * @property {Object} [containerStyle] - Inline styles for the icon container.
 * @property {string} [href] - Link URL if the icon is rendered as a link (`isLink` is true).
 * @property {string} [rel] - Specifies the relationship between the current document and the linked URL (used when `isLink` is true, defaults to "noopener noreferrer").
 */
const ActionIcon = ({
  id,
  disabled,
  onClick,
  active,
  Icon = <></>,
  iconProps,
  styleType = STYLE_TYPES.NEUTRAL,
  className,
  title,
  containerStyle = {},
  isLink = false,
  href,
  rel = 'noopener noreferrer',
  dataTestId = id || '',
  target = '_blank',
}) => (
  <StyledActionIcon
    type="button"
    id={id}
    active={active}
    data-testid={dataTestId}
    onClick={onClick}
    disabled={disabled}
    variant={styleType}
    className={cls(className)}
    style={{
      ...containerStyle,
    }}
    title={title}
    height={iconProps?.height}
    width={iconProps?.width}
    {...((isLink || href) && !disabled && { as: 'a', href, rel, target })}
  >
    <Icon {...iconProps} />
  </StyledActionIcon>
)

ActionIcon.STYLE_TYPES = STYLE_TYPES

export default ActionIcon
